<template>
  <v-dialog v-model="dialogWorkflow" max-width="500px" @keydown.stop.esc="closeWorkflow">
    <template v-slot:activator="{ props }">
      <slot name="activator" :props="props">
        <v-btn
          v-bind="props"
          rounded="lg"
          variant="flat"
          density="default"
          :color="buttonTheme === 'dark' ? '#363a40' : 'grey'"
          v-bind:class="{ 'primary--text': buttonTheme === 'light'}"
        >
          {{ buttonText }}
        </v-btn>
      </slot>
    </template>

    <v-card>
      <v-container class="pr-0">
        <v-row class="v-container--fluid justify-space-between align-top">
          <v-card-title>
            <span v-if="editWorkflow && editWorkflow.id" class="text-h5">Edit Workflow</span>
            <span v-else class="text-h5">New Workflow</span>
          </v-card-title>

          <v-btn variant="text" icon color="grey" @click="closeWorkflow">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-row>
      </v-container>

      <v-container grid-list-md>
        <v-form ref="formWorkflow" v-model="valid">
          <v-col cols="12">
            <v-select
              v-if="!projectId && !editWorkflow.id"
              :rules="[rules.required]"
              v-model="selectProjectId"
              label="Select project"
              no-data-text="No projects (click New Project)"
              item-value="project.id"
              item-title="project.name"
              :items="memberships"
              variant="outlined"
              density="default"
            />

            <v-text-field
              name="workflow-name"
              v-model="editWorkflow.name"
              :rules="[rules.required, rules.counter]"
              label="Workflow name"
              v-on:keyup.enter="saveWorkflow"
              :focused="true"
              :autofocus="true"
              :persistent-hint="editWorkflow.status === 2"
              :hint="editWorkflow.status === 2 ? 'Workflow is locked' : ''"
              :disabled="editWorkflow.status === 2"
              ref="workflowName"
              variant="outlined"
              density="default"
            >
            </v-text-field>
            <!-- keyup.enter doesn't work without this row, seems like a bug in vue or vuetify -->
            <v-text-field class="hidden" disabled style="display: none;"></v-text-field>
          </v-col>
        </v-form>
      </v-container>

      <v-card-actions class="justify-end">
        <v-btn rounded="lg" size="large" variant="outlined" color="gray" @click="closeWorkflow">Cancel</v-btn>
        <v-btn rounded="lg" size="large" color="primary" variant="flat" :disabled="editWorkflow.status === 2"
               :loading="loading" @click="saveWorkflow">
          {{ editWorkflow && editWorkflow.id ? 'Update' : 'Add' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import workflowApi from '@/api/workflowApi'
import { mapState } from 'vuex'
import { EventBus } from '@/event-bus/event-bus'

export default {
  props: {
    setWorkflow: {
      type: Boolean,
      default: false
    },
    buttonText: {
      type: String,
      default: ''
    },
    buttonTheme: {
      type: String,
      default: ''
    },
    memberships: {
      type: Array,
      default: () => []
    }
  },
  data: () => ({
    dialogWorkflow: false,
    valid: true,
    rules: {
      required: value => !!value || 'Required.',
      counter: value => (value && value.length <= 40) || 'Max 40 characters'
    },
    editWorkflow: {
      name: ''
    },
    selectProjectId: null,
    loading: false
  }),
  mounted () {
    if (this.projectId || this.filteredProjectMemberships?.length) {
      this.selectProjectId = this.projectId || this.filteredProjectMemberships[0]?.project?.id
    }
  },
  computed: {
    ...mapState({
      workflows: state => state.workflows.workflows
    }),
    projectId () {
      return this.$route.params.projectId
    },

    filteredProjectMemberships () {
      return this.memberships.filter(member => {
        return member?.project?.organisation?.user?.id === this.$store.state?.userId
      })
    }
  },
  methods: {
    async saveWorkflow () {
      const { valid } = await this.$refs.formWorkflow.validate()
      if (valid) {
        // Get stored workflows and use the projectId from the first workflow (assuming all share the same projectId).
        // Check if we need to fetch workflows for the selected project to verify name duplicates.
        if (this?.workflows?.length > 0) {
          const currentStoredProjectId = this?.workflows?.[0].projectId
          if (this.selectProjectId && this.selectProjectId !== currentStoredProjectId) {
            await this.$store.dispatch('workflows/listWorkflows', this.selectProjectId)
          }
        } else {
          await this.$store.dispatch('workflows/listWorkflows', this.selectProjectId)
        }
        const index = (this.workflows || []).findIndex(ws => (ws.name === this.editWorkflow.name && this.editWorkflow.id !== ws.id))
        if (index >= 0) {
          alert('A workflow with this name already exists.')
          return
        }

        this.editWorkflow.projectId = this.projectId || this.selectProjectId

        this.loading = true

        if (this.editWorkflow.id) {
          await workflowApi.updateWorkflowName({
            ...this.editWorkflow,
            updateNote: {
              action: 'changed workflow name to',
              target: this.editWorkflow.name
            }
          })
        } else {
          const id = await workflowApi.createWorkflow(this.editWorkflow)
          if (this.setWorkflow && id) {
            // Only emit get-workflow if we're already on a workflow page (quick action from another workflow)
            const isOnWorkflowPage = !!this.$route.params.id
            await this.$router.push(`/workflow/${this.editWorkflow.projectId}/${id}`)

            if (isOnWorkflowPage) {
              EventBus.$emit('get-workflow', id)
            }
          }
        }
        this.loading = false
        this.closeWorkflow()
        this.$emit('onsave')
      }
    },
    closeWorkflow (e) {
      e?.preventDefault()
      this.dialogWorkflow = false
    },
    openWorkflowDialog () {
      this.dialogWorkflow = true
    },
    setItem (item) {
      this.editWorkflow = item
    },
    setProject (projectId) {
      this.selectProjectId = projectId
    },
    selectAllTextInInput () {
      const inputElement = this.$refs.workflowName.$el.querySelector('input')
      inputElement.focus()
      inputElement.select()
    },
    getNextWorkflowName (workflows) {
      let number = this.workflows ? this.workflows.length + 1 : 1
      let res = 'Workflow ' + number
      while (workflows?.findIndex(ws => (ws.name === res)) >= 0) {
        number++
        res = 'Workflow ' + number
      }
      return res
    }
  },
  watch: {
    dialogWorkflow (val) {
      if (!val) {
        this.editWorkflow = {
          name: ''
        }
        if (this.$refs.formWorkflow) {
          this.$refs.formWorkflow.reset()
        }
      } else if (!this.editWorkflow || !this.editWorkflow.id) {
        if (this.$refs.formWorkflow) {
          this.$refs.formWorkflow.reset()
        }
        this.editWorkflow = {
          name: this.getNextWorkflowName(this.workflows)
        }
        this.$nextTick(() => {
          this.selectAllTextInInput()
        })
      }
    }
  }
}
</script>

<style scoped>
.tabs-bar-button {
  align-self: center;
}
</style>
